import { ConditionalWrapper } from '@shared/ui/conditional-wrapper';
import { type CSSProperties, type FC, type TdHTMLAttributes, useContext } from 'react';

import { TableContext } from '../table-context';
import { Flex, Tooltip } from 'antd';
import { Text, type TypographyVariant } from '@shared/ui/antd';

const MAX_TEXT_LENGTH = 80;

export type TCellTextTooltipProps = TdHTMLAttributes<HTMLTableCellElement> & {
  index?: number;
  text?: string | null;
  variant?: TypographyVariant;
  fontWeight?: React.CSSProperties['fontWeight'];
  maxLength?: number;
  children?: React.ReactNode;
  childrenPosition?: 'start' | 'end';
  textStyle?: CSSProperties;
};

export const TCellTextTooltip: FC<TCellTextTooltipProps> = ({
  text,
  variant = 'md',
  fontWeight = 400,
  maxLength = MAX_TEXT_LENGTH,
  index,
  children: propChildren,
  childrenPosition = 'start',
  textStyle,
  ...props
}) => {
  const { columnsConfiguration } = useContext(TableContext);

  const column = index === undefined ? null : columnsConfiguration[index];
  const width = column?.width;

  const hasLongText = text && text?.length > maxLength;
  const textSlice = hasLongText ? `${text.slice(0, maxLength)}...` : text;

  return (
    <td style={{ width }} {...props}>
      <ConditionalWrapper
        condition={hasLongText || false}
        wrapper={(children) => (
          <Tooltip
            title={text}
            placement="topLeft"
            style={{ textAlign: 'left', maxWidth: '600px' }}
          >
            {children}
          </Tooltip>
        )}
      >
        <Flex gap="8px" align="center">
          {childrenPosition === 'start' && propChildren}

          <Text variant={variant} fontWeight={fontWeight} style={textStyle}>
            {textSlice}
          </Text>

          {childrenPosition === 'end' && propChildren}
        </Flex>
      </ConditionalWrapper>
    </td>
  );
};
